import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import { showToast } from "../../../../core/utils/alert";
import { RegistrationFeeSharePayloadType } from "../../../fee-share/registrationFeeShare/types/registrationFeeShareRequest";
import { useRegistrationFeeShare } from "../../../fee-share/registrationFeeShare/hooks/useRegistrationFeeShare";
import { useCourse } from "../../../configuration/course/hooks/useCourse";
import { useFeeType } from "../../../feetypes/hooks/useFeeType";
import { useCenter } from "../../../peoples/centers/hooks/useCenter";
import { useRegistrationFeeConfig } from "../../../feeconfig/registrationFeeConfig/hooks/useRegistrationFeeConfig";
import CommonSelect from "../../../../core/common/commonSelect";
import MultiSelect, { Option } from "../../../../core/common/MultiSelect";

const CenterRegFeeShare = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const { id: centerId } = useParams<{ id: string }>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const courseIdFromQuery = queryParams.get("course_id");
  const isEditMode = queryParams.get("edit") === "true";
  const shareIdFromQuery = queryParams.get("share_id");

  const [isEdit, setIsEdit] = useState<boolean>(isEditMode);

  const { getCourseState } = useCourse();
  const { getFeeTypeState } = useFeeType();
  const { useGetCenterById } = useCenter({ skip: true });
  const {
    addFeeShare,
    updateFeeShare,
    isAdding,
    isUpdating,
    useGetFeeShareById,
  } = useRegistrationFeeShare();
  const { useGetFeeConfigById: useGetRegistrationFeeById } =
    useRegistrationFeeConfig();

  const { data: centerData } = useGetCenterById(centerId || "");

  const { feeShare, loading: isLoadingFeeShare } = useGetFeeShareById(
    isEditMode && shareIdFromQuery ? shareIdFromQuery : ""
  );

  const { courses, isLoading: isLoadingCourses } = getCourseState();
  const { feetypes, isLoading: isLoadingFeeTypes } = getFeeTypeState();

  const [registrationFeeConfigId, setRegistrationFeeConfigId] =
    useState<string>("");

  const {
    feeConfig: registrationFeeConfig,
    loading: isLoadingRegistrationFeeConfig,
  } = useGetRegistrationFeeById(registrationFeeConfigId);

  const [registrationFeeAmount, setRegistrationFeeAmount] = useState<number>(0);

  const registrationFeeTypeId = useMemo(() => {
    const registrationFeeType = feetypes.find(
      (feetype) => feetype.type === "registrationfee"
    );
    return registrationFeeType?.id || 0;
  }, [feetypes]);

  const feeTypeOption = registrationFeeTypeId
    ? { value: registrationFeeTypeId.toString(), label: "Registration Fee" }
    : null;

  const courseOptions = courses.map((course) => ({
    value: course.id.toString(),
    label: course.name,
  }));

  const shareTypeOptions = [
    { value: "fixed", label: "Fixed Amount (₹)" },
    { value: "percent", label: "Percentage (%)" },
  ];

  const [formData, setFormData] = useState<RegistrationFeeSharePayloadType>({
    course_id: 0,
    type_id: registrationFeeTypeId,
    share_type: "",
    share_amount: 0,
    all_centers: false,
    center_ids: centerId ? [parseInt(centerId, 10)] : [],
  });

  const [selectedCourse, setSelectedCourse] = useState("");

  useEffect(() => {
    if (centerId) {
      const centerIdNum = parseInt(centerId, 10);
      if (!formData.center_ids?.includes(centerIdNum)) {
        setFormData(prev => ({
          ...prev,
          center_ids: [centerIdNum],
          all_centers: false
        }));
      }
    }
  }, [centerId, formData.center_ids]);

  useEffect(() => {
    if (courseIdFromQuery) {
      setFormData((prev) => ({
        ...prev,
        course_id: parseInt(courseIdFromQuery, 10),
      }));
      setSelectedCourse(courseIdFromQuery);
    }
  }, [courseIdFromQuery]);

  useEffect(() => {
    if (registrationFeeTypeId && formData.type_id === 0) {
      setFormData((prev) => ({
        ...prev,
        type_id: registrationFeeTypeId,
      }));
    }
  }, [registrationFeeTypeId]);

  useEffect(() => {
    if (formData.course_id) {
      setRegistrationFeeConfigId(formData.course_id.toString());
    } else {
      setRegistrationFeeConfigId("");
    }
  }, [formData.course_id]);

  useEffect(() => {
    if (registrationFeeConfig?.registrationfee) {
      const feeAmount =
        parseFloat(registrationFeeConfig.registrationfee.fee) || 0;
      setRegistrationFeeAmount(feeAmount);
    } else {
      setRegistrationFeeAmount(0);
    }
  }, [registrationFeeConfig]);

  useEffect(() => {
    if (isEditMode && centerId && courseIdFromQuery) {
      setIsEdit(true);
    }
  }, [isEditMode, centerId, courseIdFromQuery]);

  useEffect(() => {
    if (isEdit && feeShare) {
      setFormData({
        course_id: feeShare.course_id || 0,
        type_id: registrationFeeTypeId,
        share_type: feeShare.share_type || "",
        share_amount: feeShare.share_amount || 0,
        all_centers: false,
        center_ids: centerId ? [parseInt(centerId, 10)] : [],
      });
      
      if (feeShare.reg_fee) {
        setRegistrationFeeAmount(feeShare.reg_fee);
      }
    }
  }, [isEdit, feeShare, registrationFeeTypeId, centerId]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target as HTMLInputElement;

    if (type === "checkbox") {
      const { checked } = e.target as HTMLInputElement;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (name === "share_amount") {
      let parsedValue = parseFloat(value) || 0;

      if (formData.share_type === "percent") {
        if (parsedValue > 100) parsedValue = 100;
        if (parsedValue < 0) parsedValue = 0;
      } else if (formData.share_type === "fixed") {
        if (parsedValue > registrationFeeAmount)
          parsedValue = registrationFeeAmount;
        if (parsedValue < 0) parsedValue = 0;
      }

      setFormData((prev) => ({ ...prev, [name]: parsedValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSelectChange = (fieldName: string, selected: any) => {
    if (selected) {
      if (
        fieldName === "share_type" &&
        selected.value === "percent" &&
        formData.share_amount > 100
      ) {
        setFormData((prev) => ({
          ...prev,
          [fieldName]: selected.value,
          share_amount: 100,
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          [fieldName]:
            fieldName === "share_type"
              ? selected.value
              : parseInt(selected.value, 10),
        }));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.course_id) {
      showToast({
        title: "Error",
        text: "Please select a course",
        icon: "error",
      });
      return;
    }

    if (!formData.share_type) {
      showToast({
        title: "Error",
        text: "Please select a share type",
        icon: "error",
      });
      return;
    }

    try {
      const payload = {
        course_id: formData.course_id,
        center_id: parseInt(centerId || "0", 10),
        reg_fee: registrationFeeAmount,
        share_type: formData.share_type,
        share_amount: formData.share_amount
      };

      let result;
      if (isEdit && shareIdFromQuery) {
        result = await updateFeeShare(shareIdFromQuery, payload);
      } else {
        result = await addFeeShare(payload);
      }

      if (result.success) {
        showToast({
          title: "Success",
          text: isEdit
            ? "Registration fee share updated successfully"
            : "Registration fee share added successfully",
          icon: "success",
        });
        navigate(-1);
      }
    } catch (error) {
      console.error("Error with fee share:", error);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleCourseChange = (option: any | null) => {
    if (option) {
      setSelectedCourse(option.value.toString());
    } else {
      setSelectedCourse("");
    }
  };

  const isLoading = isEdit && (isLoadingFeeShare || !feeShare);

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="mb-1">
                {isEdit ? "Edit" : "Add"} Registration Fee Share for {centerData?.data?.center?.name || "Center"}
              </h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.centerList}>Centers</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={`/center-detail/${centerId}`}>Center Details</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {isEdit ? "Edit" : "Add"} Registration Fee Share
                  </li>
                </ol>
              </nav>
            </div>
          </div>

          {isLoading ? (
            <div className="card">
              <div className="card-body text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading registration fee share data...</p>
              </div>
            </div>
          ) : (
            <div className="row">
              <div className="col-md-12">
                <form onSubmit={handleSubmit}>
                  <div className="card mb-3">
                    <div className="card-header bg-light">
                      <div className="d-flex align-items-center">
                        <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                          <i className="ti ti-info-square-rounded fs-16" />
                        </span>
                        <h4 className="text-dark">Fee Share Information</h4>
                      </div>
                    </div>
                    <div className="card-body pb-1">
                      <div className="row">

                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              Course <span className="text-danger">*</span>
                            </label>
                            <CommonSelect
                              className="select"
                              options={courseOptions}
                              onChange={(selected) => {
                                handleCourseChange(selected);
                                handleSelectChange("course_id", selected);
                              }}
                              value={courseOptions.find(
                                (option) =>
                                  option.value === formData.course_id.toString()
                              )}
                              placeholder={
                                isLoadingCourses
                                  ? "Loading courses..."
                                  : "Select Course"
                              }
                              isDisabled={!!courseIdFromQuery}
                              required
                            />
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              Fee Type <span className="text-danger">*</span>
                            </label>
                            <CommonSelect
                              className="select"
                              value={feeTypeOption || undefined}
                              options={feeTypeOption ? [feeTypeOption] : []}
                              isDisabled={true}
                              placeholder="Registration Fee"
                            />
                          </div>
                        </div>

                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              Share Type <span className="text-danger">*</span>
                            </label>
                            <CommonSelect
                              className="select"
                              options={shareTypeOptions}
                              onChange={(selected) =>
                                handleSelectChange("share_type", selected)
                              }
                              value={shareTypeOptions.find(
                                (option) => option.value === formData.share_type
                              )}
                              placeholder="Select Share Type"
                              required
                            />
                          </div>
                        </div>
                        {registrationFeeAmount > 0 && (
                          <>
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label">
                                  Registration Fee Amount
                                </label>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={`₹${registrationFeeAmount.toLocaleString()}`}
                                  disabled
                                  readOnly
                                />
                                <small className="form-text text-muted">
                                  This is the current registration fee amount
                                  for this course.
                                </small>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label">
                                  {formData.share_type === "percent" ? "Share Percentage" : "Share Amount"}{" "}
                                  <span className="text-danger">*</span>
                                </label>
                                <div className="input-group">
                                  <input
                                    type="number"
                                    className="form-control"
                                    name="share_amount"
                                    value={formData.share_amount || ""}
                                    onChange={handleInputChange}
                                    min={0}
                                    max={
                                      formData.share_type === "percent"
                                        ? 100
                                        : registrationFeeAmount
                                    }
                                    required
                                  />
                                  <span className="input-group-text">
                                    {formData.share_type === "fixed"
                                      ? "₹"
                                      : "%"}
                                  </span>
                                </div>
                                {formData.share_type === "percent" ? (
                                  <small className="form-text text-muted">
                                    Maximum percentage is 100%.
                                    {registrationFeeAmount > 0 && (
                                      <span className="ms-1">
                                        (Calculated amount: ₹{((registrationFeeAmount * formData.share_amount) / 100).toFixed(2)})
                                      </span>
                                    )}
                                  </small>
                                ) : (
                                  <small className="form-text text-muted">
                                    Maximum amount is ₹
                                    {registrationFeeAmount.toLocaleString()}.
                                  </small>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="text-end mb-3">
                    <button
                      type="button"
                      className="btn btn-light me-3"
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isAdding || isUpdating}
                    >
                      {isAdding || isUpdating ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-1"
                            role="status"
                          ></span>
                          {isEdit ? "Updating..." : "Saving..."}
                        </>
                      ) : (
                        isEdit ? "Update Registration Fee Share" : "Save Registration Fee Share"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CenterRegFeeShare;
